import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import { useAuth } from "../../../auth/useAuth";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  SELLER_SUBMISSION_ADMIN_PATH,
  SELLER_WORKFLOW_ACTIONS,
  formatAdminDate,
  formatCopPrice,
  formatLocation,
  formatSellerReviewStatus,
  formatSellerType,
  formatSellerWorkflowStatus,
  formatSellerContactStatus,
  formatSellerFollowUpPriority,
  formatVehicleSummary,
  sellerReviewStatusBadgeClass,
  sellerWorkflowStatusBadgeClass,
  sellerContactStatusBadgeClass,
} from "../../../constants/sellerSubmissionAdminConstants";
import {
  fetchAdminSellerSubmissions,
  updateAdminSellerSubmissionWorkflow,
} from "../../../services/sellerSubmissionAdminApi";

const PAGE_SIZE = 25;

function AdminSellerSubmissionsListPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusyId, setActionBusyId] = useState(null);

  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const reviewStatus = searchParams.get("reviewStatus") || "";
  const workflowStatus = searchParams.get("workflowStatus") || "";
  const sellerType = searchParams.get("sellerType") || "";
  const city = searchParams.get("city") || "";
  const vehicleMake = searchParams.get("vehicleMake") || "";
  const vehicleYear = searchParams.get("vehicleYear") || "";
  const sourcePage = searchParams.get("sourcePage") || "";
  const converted = searchParams.get("converted") || "";
  const hasPhotos = searchParams.get("hasPhotos") || "";
  const includeArchived = searchParams.get("includeArchived") || "";
  const includeSpam = searchParams.get("includeSpam") || "";
  const contactStatus = searchParams.get("contactStatus") || "";
  const followUpPriority = searchParams.get("followUpPriority") || "";
  const followUpOverdue = searchParams.get("followUpOverdue") || "";
  const followUpDueToday = searchParams.get("followUpDueToday") || "";
  const notContacted = searchParams.get("notContacted") || "";
  const q = searchParams.get("q") || "";

  const loadSubmissions = useCallback(() => {
    setLoading(true);
    setError("");
    fetchAdminSellerSubmissions({
      page,
      limit: PAGE_SIZE,
      reviewStatus: reviewStatus || undefined,
      workflowStatus: workflowStatus || undefined,
      sellerType: sellerType || undefined,
      city: city || undefined,
      vehicleMake: vehicleMake || undefined,
      vehicleYear: vehicleYear || undefined,
      sourcePage: sourcePage || undefined,
      converted: converted || undefined,
      hasPhotos: hasPhotos || undefined,
      includeArchived: includeArchived || undefined,
      includeSpam: includeSpam || undefined,
      contactStatus: contactStatus || undefined,
      followUpPriority: followUpPriority || undefined,
      followUpOverdue: followUpOverdue || undefined,
      followUpDueToday: followUpDueToday || undefined,
      notContacted: notContacted || undefined,
      q: q || undefined,
    })
      .then((data) => {
        setSubmissions(data.submissions || []);
        setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
      })
      .catch((err) => setError(err.message || "No pudimos cargar las solicitudes. Intenta nuevamente."))
      .finally(() => setLoading(false));
  }, [
    page,
    reviewStatus,
    workflowStatus,
    sellerType,
    city,
    vehicleMake,
    vehicleYear,
    sourcePage,
    converted,
    hasPhotos,
    includeArchived,
    includeSpam,
    contactStatus,
    followUpPriority,
    followUpOverdue,
    followUpDueToday,
    notContacted,
    q,
  ]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const goToPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next);
  };

  const hasFilters = Boolean(
    reviewStatus ||
      workflowStatus ||
      sellerType ||
      city ||
      vehicleMake ||
      vehicleYear ||
      sourcePage ||
      converted ||
      hasPhotos ||
      includeArchived ||
      includeSpam ||
      contactStatus ||
      followUpPriority ||
      followUpOverdue ||
      followUpDueToday ||
      notContacted ||
      q
  );

  const quickMarkReviewed = async (submissionId) => {
    if (!canManage) return;
    setActionBusyId(submissionId);
    try {
      await updateAdminSellerSubmissionWorkflow(submissionId, {
        action: SELLER_WORKFLOW_ACTIONS.MARK_REVIEWED,
      });
      loadSubmissions();
    } catch {
      // keep list stable; detail page has richer errors
    } finally {
      setActionBusyId(null);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Vendedores"
        title="Solicitudes de vendedores"
        description="Bandeja interna para revisar envíos del flujo Vender mi carro."
      />
      <AdminAutomotiveNav active="seller-submissions" />

      <main className="admin-console__main wrap">
        <div className="dealer-inventory-toolbar" style={{ marginBottom: "0.75rem" }}>
          <Link className="dealer-portal-btn dealer-portal-btn--primary" to="/admin/seller-submissions/monitoring">
            Monitoreo
          </Link>
        </div>
        <div className="dealer-inventory-toolbar">
          <input
            placeholder="Buscar marca, modelo, ciudad o contacto"
            value={q}
            onChange={(event) => updateFilter("q", event.target.value.trim())}
          />
          <select value={reviewStatus} onChange={(event) => updateFilter("reviewStatus", event.target.value)}>
            <option value="">Estado de revisión</option>
            {["new", "reviewed", "needs_info", "contacted", "approved", "rejected", "converted_to_listing", "archived", "spam"].map(
              (value) => (
                <option key={value} value={value}>
                  {formatSellerReviewStatus(value)}
                </option>
              )
            )}
          </select>
          <select value={workflowStatus} onChange={(event) => updateFilter("workflowStatus", event.target.value)}>
            <option value="">Flujo de trabajo</option>
            {["not_started", "in_review", "waiting_on_seller", "ready_for_listing", "converted", "closed"].map(
              (value) => (
                <option key={value} value={value}>
                  {formatSellerWorkflowStatus(value)}
                </option>
              )
            )}
          </select>
          <select value={sellerType} onChange={(event) => updateFilter("sellerType", event.target.value)}>
            <option value="">Tipo de vendedor</option>
            {["individual", "dealer", "small_lot", "unknown"].map((value) => (
              <option key={value} value={value}>
                {formatSellerType(value)}
              </option>
            ))}
          </select>
          <input placeholder="Ciudad" value={city} onChange={(event) => updateFilter("city", event.target.value)} />
          <input
            placeholder="Marca"
            value={vehicleMake}
            onChange={(event) => updateFilter("vehicleMake", event.target.value)}
          />
          <input
            placeholder="Año"
            value={vehicleYear}
            onChange={(event) => updateFilter("vehicleYear", event.target.value)}
          />
          <select value={converted} onChange={(event) => updateFilter("converted", event.target.value)}>
            <option value="">Conversión</option>
            <option value="true">Convertido</option>
            <option value="false">Sin convertir</option>
          </select>
          <select value={hasPhotos} onChange={(event) => updateFilter("hasPhotos", event.target.value)}>
            <option value="">Fotos</option>
            <option value="true">Con fotos</option>
            <option value="false">Sin fotos</option>
          </select>
          <select value={contactStatus} onChange={(event) => updateFilter("contactStatus", event.target.value)}>
            <option value="">Estado de contacto</option>
            {[
              "not_contacted",
              "contact_attempted",
              "contacted",
              "needs_follow_up",
              "waiting_on_seller",
              "seller_unresponsive",
              "seller_declined",
              "ready_to_publish",
              "closed",
            ].map((value) => (
              <option key={value} value={value}>
                {formatSellerContactStatus(value)}
              </option>
            ))}
          </select>
          <select value={followUpPriority} onChange={(event) => updateFilter("followUpPriority", event.target.value)}>
            <option value="">Prioridad seguimiento</option>
            <option value="high">Alta</option>
            <option value="normal">Normal</option>
            <option value="low">Baja</option>
          </select>
          <label>
            <input
              type="checkbox"
              checked={followUpOverdue === "true"}
              onChange={(event) => updateFilter("followUpOverdue", event.target.checked ? "true" : "")}
            />{" "}
            Seguimiento vencido
          </label>
          <label>
            <input
              type="checkbox"
              checked={followUpDueToday === "true"}
              onChange={(event) => updateFilter("followUpDueToday", event.target.checked ? "true" : "")}
            />{" "}
            Seguimiento hoy
          </label>
          <label>
            <input
              type="checkbox"
              checked={notContacted === "true"}
              onChange={(event) => updateFilter("notContacted", event.target.checked ? "true" : "")}
            />{" "}
            No contactado
          </label>
          <input
            placeholder="Origen (source page)"
            value={sourcePage}
            onChange={(event) => updateFilter("sourcePage", event.target.value)}
          />
          <label>
            <input
              type="checkbox"
              checked={includeArchived === "true"}
              onChange={(event) => updateFilter("includeArchived", event.target.checked ? "true" : "")}
            />{" "}
            Incluir archivados
          </label>
          <label>
            <input
              type="checkbox"
              checked={includeSpam === "true"}
              onChange={(event) => updateFilter("includeSpam", event.target.checked ? "true" : "")}
            />{" "}
            Incluir spam
          </label>
        </div>

        {loading ? (
          <AdminStatePanel variant="loading" message="Cargando solicitudes…" />
        ) : error ? (
          <AdminStatePanel variant="error" message="No pudimos cargar las solicitudes. Intenta nuevamente." onRetry={loadSubmissions} retryLabel="Reintentar" />
        ) : submissions.length === 0 ? (
          <AdminStatePanel
            variant="empty"
            message={hasFilters ? "No encontramos solicitudes con esos filtros." : "No hay solicitudes de vendedores todavía."}
          />
        ) : (
          <>
            <AdminTableContainer>
              <table className="dealer-inventory-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Vehículo</th>
                    <th>Vendedor</th>
                    <th>Ubicación</th>
                    <th>Precio</th>
                    <th>Estado</th>
                    <th>Origen</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id}>
                      <td>
                        {formatAdminDate(submission.createdAt)}
                        <div className="admin-table-subtext">
                          Actualizado: {formatAdminDate(submission.updatedAt)}
                        </div>
                      </td>
                      <td>
                        <strong>{formatVehicleSummary(submission)}</strong>
                        {submission.vehicleMileageKm != null ? (
                          <div className="admin-table-subtext">{submission.vehicleMileageKm.toLocaleString("es-CO")} km</div>
                        ) : null}
                      </td>
                      <td>
                        <div>{submission.sellerDisplayName || "—"}</div>
                        <div className="admin-table-subtext">{formatSellerType(submission.sellerType)}</div>
                        {submission.sellerPhone ? (
                          <div className="admin-table-subtext">{submission.sellerPhone}</div>
                        ) : null}
                      </td>
                      <td>{formatLocation(submission)}</td>
                      <td>{formatCopPrice(submission.askingPrice, submission.currency)}</td>
                      <td>
                        <span className={sellerReviewStatusBadgeClass(submission.reviewStatus)}>
                          {formatSellerReviewStatus(submission.reviewStatus)}
                        </span>
                        <div className="admin-table-subtext">
                          <span className={sellerWorkflowStatusBadgeClass(submission.workflowStatus)}>
                            {formatSellerWorkflowStatus(submission.workflowStatus)}
                          </span>
                        </div>
                        {submission.convertedListingId ? (
                          <div className="admin-table-subtext">
                            <Link to={`/admin/listings/${submission.convertedListingId}`}>Borrador creado</Link>
                          </div>
                        ) : submission.workflowStatus === "ready_for_listing" ? (
                          <div className="admin-table-subtext">Listo para publicación</div>
                        ) : null}
                        <div className="admin-table-subtext" style={{ marginTop: "0.35rem" }}>
                          <span className={sellerContactStatusBadgeClass(submission.contactStatus)}>
                            {formatSellerContactStatus(submission.contactStatus)}
                          </span>
                          {submission.followUpOverdue ? (
                            <span className="lead-badge lead-badge--danger" style={{ marginLeft: "0.35rem" }}>
                              Vencido
                            </span>
                          ) : submission.followUpDueToday ? (
                            <span className="lead-badge lead-badge--warning" style={{ marginLeft: "0.35rem" }}>
                              Hoy
                            </span>
                          ) : null}
                        </div>
                        {submission.nextFollowUpAt ? (
                          <div className="admin-table-subtext">
                            Seguimiento: {formatAdminDate(submission.nextFollowUpAt)}
                          </div>
                        ) : null}
                        {submission.contactAttemptsCount > 0 ? (
                          <div className="admin-table-subtext">
                            Intentos: {submission.contactAttemptsCount}
                            {submission.followUpPriority && submission.followUpPriority !== "normal"
                              ? ` · ${formatSellerFollowUpPriority(submission.followUpPriority)}`
                              : null}
                          </div>
                        ) : null}
                      </td>
                      <td>{submission.sourcePage || "—"}</td>
                      <td>
                        {canManage && submission.reviewStatus === "new" ? (
                          <button
                            type="button"
                            className="dealer-portal-btn"
                            disabled={actionBusyId === submission.id}
                            onClick={() => quickMarkReviewed(submission.id)}
                          >
                            Revisado
                          </button>
                        ) : null}{" "}
                        <Link className="dealer-portal-btn" to={`${SELLER_SUBMISSION_ADMIN_PATH}/${submission.id}`}>
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>

            <div className="admin-pagination">
              <button type="button" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                Anterior
              </button>
              <span>
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} solicitudes)
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => goToPage(page + 1)}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminSellerSubmissionsListPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminSellerSubmissionsListPageContent />
    </RequirePermission>
  );
}
